import { Tooltip } from '@biq/apex-ui';
import { Grid, Icon, Typography } from '@mui/material';
import { alpha } from '@mui/system';
import { themeObject, validationsMessages } from 'lib';
import { walletsAndGroupsCommonStyles } from 'pages/wallets-and-groups/wallets-and-groups-helper';
import * as yup from 'yup';
import CancelIcon from '@mui/icons-material/Cancel';

export interface CreateAndUpdateUserGroupFormData {
    name: string;
    description: string;
    group: {
        id: string;
        value: string;
        header: string;
        subtitle: string;
    }[];
}

const validateNameRegex = /^[a-zA-Z0-9 _-]+$/;
const validateNameMessage = 'Enter a valid group name';

export const CreateAndUpdateUserGroupSchema = yup.object({
    name: yup
        .string()
        .required(validationsMessages.required)
        .matches(validateNameRegex, validateNameMessage),
    description: yup.string(),
    group: yup
        .array()
        .of(
            yup.object({
                id: yup.string(),
                value: yup.string(),
                header: yup.string(),
                subtitle: yup.string(),
            })
        )
        .required(validationsMessages.required)
        .min(1, validationsMessages.required),
});

export const createAndUpdateUserGroupDefaults = {
    name: '',
    description: '',
    group: [],
};

export interface GroupMembersData {
    id: string;
    value: string;
    header: string;
    subtitle: string;
}

export interface AddGroupMembersProps {
    openAddGroupMemberModal: boolean;
    handleAddGroupMembersModal: () => void;
    groupMembersData: GroupMembersData[];
    existingGroupMembers: GroupMembersData[];
    onAddNewGroupMembers: (values: GroupMembersData[]) => void;
}

export const updateUserGroupStyles = {
    groupMembersTextContainerStyles: {
        mt: '25.5px',
        mb: '-5px',
    },
    groupMembersTextStyles: {
        ...themeObject.typography.h2,
        fontSize: '24px',
        lineHeight: '28.18px',
        color: themeObject.palette.primary[700],
    },
    mainContainerStyles: {
        ...walletsAndGroupsCommonStyles.containerStyles,
        flexDirection: 'row',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        width: '100%',
    },
    addMemberButtonContainerStyles: {
        ml: 2,
        mt: '20px',
    },
    addMemberButtonStyles: {
        borderRadius: '64px',
        width: '172px',
        height: '40px',
        boxShadow: `0px 1px 18px 0px ${alpha(
            themeObject.palette.grayScale[700],
            0.12
        )},0px 6px 10px 0px ${alpha(themeObject.palette.grayScale[700], 0.14)}`,
        '& .MuiTypography-root': {
            fontFamily: 'Red Hat Text',
            fontSize: '14px',
            fontWeight: 700,
            lineHeight: '24px',
            color: themeObject.palette.grayScale[700],
            ml: '2px',
        },
    },
    textBoldStyles: {
        ...themeObject.typography.body.default,
        ...themeObject.typography.body.body1,
        ...themeObject.typography.body.medium,
        lineHeight: '21.17px',
        textWrap: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        color: themeObject.palette.grayScale[800],
    },
    textStyles: {
        ...themeObject.typography.body.default,
        ...themeObject.typography.body.body2,
        ...themeObject.typography.body.regular,
        lineHeight: '18.52px',
        textWrap: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        color: themeObject.palette.grayScale[800],
    },
    crossIconContainerStyles: {
        ml: '15px',
    },
    crossIconStyles: {
        cursor: 'pointer',
        '& .MuiSvgIcon-root': {
            height: '16px',
            width: '16px',
            color: themeObject.palette.grayScale[500],
        },
    },
    listComponentValueContainerStyles: {
        width: '30%',
    },
    listComponentValueWithTootlipStyles: {
        width: '86%',
    },
    listComponentValueWithTooltipContainerStyles: {
        display: 'flex',
        width: '25%',
    },
};

const memberListDataContainerStyles = (readOnly: boolean): object => {
    return {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        mt: '12px',
        pl: 2,
        '& .cross-icon-container': {
            visibility: 'hidden',
            opacity: 0,
        },
        ...(!readOnly && {
            '&:hover': {
                backgroundColor: alpha(themeObject.palette.primary[50], 0.65),
                '& .cross-icon-container': {
                    visibility: 'visible',
                    opacity: 1,
                },
            },
        }),
    };
};

export const addGroupMembersModalStyles = {
    dialog: {
        '& .MuiPaper-root': {
            width: '684px',
            maxWidth: '684px',
            maxHeight: '75%',
            '& .MuiDialogContent-root': {
                p: '16px 32px 16px 32px',
                overflow: 'auto',
                maxHeight: 'calc(100% - 142px)',
            },
            '& .MuiDialogActions-root': {
                p: '16px 34px 16px 8px',
            },
        },
    },
    container: {
        display: 'flex',
        flexDirection: 'column',
    },
    imageContainer: {
        display: 'flex',
        justifyContent: 'center',
        mb: '20px',
    },
    listboxStyles: {
        maxHeight: '260px',
    },
};

export const combinedSystemAndUserWalletsData = (walletsData: any): any[] => {
    const combinedGroupMembers = [
        ...(walletsData?.data?.userWallets?.map((userWallet: any) => ({
            id: userWallet.id ?? '',
            value: userWallet.fullName || userWallet.email || '',
            header: userWallet.fullName || '',
            subtitle: userWallet.email || '',
        })) || []),
    ];
    combinedGroupMembers.sort((a: any, b: any) => {
        return a.header.toLowerCase().localeCompare(b.header.toLowerCase());
    });
    return combinedGroupMembers;
};

export const ListComponent = ({
    value,
    handleRemoveData,
    readOnly,
}: {
    value: any;
    handleRemoveData: (id: string) => void;
    readOnly: boolean;
}): React.JSX.Element => {
    return (
        <Grid sx={memberListDataContainerStyles(readOnly)}>
            <Grid sx={updateUserGroupStyles.listComponentValueContainerStyles}>
                <Typography sx={updateUserGroupStyles.textBoldStyles}>
                    {value.header}
                </Typography>
            </Grid>
            <Grid sx={updateUserGroupStyles.listComponentValueContainerStyles}>
                <Typography sx={updateUserGroupStyles.textStyles}>
                    {value.subtitle}
                </Typography>
            </Grid>
            <Grid
                sx={
                    updateUserGroupStyles.listComponentValueWithTooltipContainerStyles
                }
            >
                <Grid
                    sx={
                        updateUserGroupStyles.listComponentValueWithTootlipStyles
                    }
                >
                    <Typography sx={updateUserGroupStyles.textStyles}>
                        {value.id}
                    </Typography>
                </Grid>
                {!readOnly ? (
                    <Grid
                        sx={updateUserGroupStyles.crossIconContainerStyles}
                        className='cross-icon-container'
                    >
                        <Tooltip title={'Remove Member'} placement='top' arrow>
                            <Icon
                                fontSize='inherit'
                                color='disabled'
                                sx={updateUserGroupStyles.crossIconStyles}
                                onClick={(): void => handleRemoveData(value.id)}
                            >
                                <CancelIcon />
                            </Icon>
                        </Tooltip>
                    </Grid>
                ) : null}
            </Grid>
        </Grid>
    );
};
